import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import {
  ICurriculumPayload,
  ICreateCurriculumResponse,
  ICurriculumResponse,
  ICurriculumResponseById,
  ICurriculumQueryParams,
  ICurriculumInactivateGatedResponse,
  ICurriculumStatusResponse,
  ICurriculumDeleteGatedResponse,
  ICurriculumDeleteSuccessResponse,
} from '../../interface';
import { IErrorResponse } from '../../interface/main.interface';
import { curriculumService } from '../../services/curriculum';
import {
  IUpdateOperationalTimePreferencePayload,
  IUpdateOperationalTimePreferenceResponse,
} from '../../interface/operationalTime.interface';

export const fetchCurriculums = createAsyncThunk<
  ICurriculumResponse,
  ICurriculumQueryParams | void,
  { rejectValue: IErrorResponse }
>('curriculum/fetch-all', async (payload, { rejectWithValue }) => {
  try {
    // If payload is void or isActive is undefined, default to true.
    // If payload.isActive is explicitly null, pass null so backend returns both active and inactive.
    const params: ICurriculumQueryParams = payload
      ? { isActive: payload.isActive === undefined ? true : payload.isActive }
      : { isActive: true };

    const response = await curriculumService.fetchCurriculums(params);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const fetchCurriculum = createAsyncThunk<
  ICurriculumResponseById,
  string,
  { rejectValue: IErrorResponse }
>('curriculum/fetch-one', async (curriculumId, { rejectWithValue }) => {
  try {
    const response = await curriculumService.fetchCurriculum(curriculumId);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const createCurriculum = createAsyncThunk<
  ICreateCurriculumResponse,
  ICurriculumPayload,
  { rejectValue: { message: string } }
>('curriculum/create', async (payload, { rejectWithValue }) => {
  try {
    const response = await curriculumService.createCurriculum(payload);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<{ message: string }>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const updateCurriculum = createAsyncThunk<
  ICreateCurriculumResponse,
  { curriculumId: string; payload: ICurriculumPayload },
  { rejectValue: { message: string } }
>('curriculum/update', async ({ curriculumId, payload }, { rejectWithValue }) => {
  try {
    const response = await curriculumService.updateCurriculum(curriculumId, payload);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<{ message: string }>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const updateOperationalTime = createAsyncThunk<
  IUpdateOperationalTimePreferenceResponse,
  { curriculumId: string; payload: IUpdateOperationalTimePreferencePayload },
  { rejectValue: IErrorResponse }
>('curriculum/update-operational-time', async ({ curriculumId, payload }, { rejectWithValue }) => {
  try {
    const response = await curriculumService.updateOperationalTime(curriculumId, payload);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

/**
 * Activate / inactivate a curriculum via PATCH /v2/curriculum/:id/status.
 *
 * The shared axios instance rejects with the response BODY directly (see the
 * response interceptor in services/api.tsx), so on failure `err` is already the
 * parsed body. Forward the structured inactivation soft-gate response
 * (errorCode + impact `data`) untouched so the screen can show the
 * move-to-Unassigned confirmation modal instead of a plain error toast.
 */
export const setCurriculumStatus = createAsyncThunk<
  ICurriculumStatusResponse,
  { curriculumId: string; status: boolean; confirmed?: boolean },
  { rejectValue: ICurriculumInactivateGatedResponse | { message: string } }
>('curriculum/set-status', async ({ curriculumId, status, confirmed }, { rejectWithValue }) => {
  try {
    const response = await curriculumService.setCurriculumStatus(curriculumId, status, confirmed);
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<{ message: string }>;
      return rejectWithValue(error.response?.data || { message: 'Unknown error' });
    }

    const gatedBody = err as Partial<ICurriculumInactivateGatedResponse> & { message?: string };
    if (gatedBody?.errorCode) {
      return rejectWithValue(gatedBody as ICurriculumInactivateGatedResponse);
    }

    return rejectWithValue({ message: gatedBody?.message || 'Unknown error' });
  }
});

/**
 * Permanently delete a curriculum via DELETE /v2/curriculum/:id (gated cascade).
 *
 * - On 200: returns ICurriculumDeleteSuccessResponse in fulfilled.payload.
 * - On a 400 gate (hard block or REQUIRES_CONFIRMATION soft gate):
 *   rejectWithValue receives the structured ICurriculumDeleteGatedResponse so the
 *   component can read `errorCode` (+ `data` impact report) and show the correct
 *   modal.
 * - On any other error: rejectWithValue receives a generic { message } object.
 *
 * NOTE: the shared axios instance (services/api.tsx) rejects with the response
 * BODY directly, so the value caught here is already the parsed body — NOT an
 * AxiosError. Forward the structured gated body untouched.
 */
export const deleteCurriculum = createAsyncThunk<
  ICurriculumDeleteSuccessResponse,
  { curriculumId: string; confirmed?: boolean },
  { rejectValue: ICurriculumDeleteGatedResponse | { message: string } }
>('curriculum/delete', async ({ curriculumId, confirmed }, { rejectWithValue }) => {
  try {
    const response = await curriculumService.deleteCurriculum(curriculumId, confirmed);
    return response.data;
  } catch (err) {
    const body = err as Partial<ICurriculumDeleteGatedResponse> & { message?: string };

    // Structured gate response (400 body carries a machine-readable errorCode) —
    // pass it through so the component can decide which modal to show.
    if (body?.errorCode) {
      return rejectWithValue(body as ICurriculumDeleteGatedResponse);
    }

    return rejectWithValue({ message: body?.message || 'Failed to delete curriculum' });
  }
});

export const curriculumActions = {
  fetchCurriculums,
  fetchCurriculum,
  createCurriculum,
  updateCurriculum,
  updateOperationalTime,
  setCurriculumStatus,
  deleteCurriculum,
};
